import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const STATE_KEY = 'cc_form_state';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "2026-09-24" -> "24 September 2026": the order property is read by customers and by Jane. */
function orderDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : iso;
}

class CCBuyForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    const ref = (name) => this.querySelector(`[data-ref="${name}"]`);
    this.refs = {
      variantId: ref('variantId'),
      quantity: ref('quantity'),
      propLine: ref('propLine'),
      propShip: ref('propShip'),
      propDate: ref('propDate'),
      propPrint: ref('propPrint'),
      lines: ref('lines'),
      ship: ref('ship'),
      date: ref('date'),
      dateError: ref('dateError'),
      qtyDec: ref('qtyDec'),
      qtyInc: ref('qtyInc'),
      qtyVal: ref('qtyVal'),
      noDate: ref('noDate'),
      noDateWrap: ref('noDateWrap'),
      submit: ref('submit'),
      submitText: ref('submitText'),
      formError: ref('formError'),
      sticky: ref('sticky'),
      stLine: ref('stLine'),
      stShip: ref('stShip'),
      stDate: ref('stDate'),
      stNoDate: ref('stNoDate'),
      stBadge: ref('stBadge'),
      stOrig: ref('stOrig'),
      stPrice: ref('stPrice'),
      stBtn: ref('stBtn'),
      stBtnText: ref('stBtnText'),
      stPrompt: ref('stPrompt'),
      stPromptBtn: ref('stPromptBtn'),
    };
    this.labels = {
      select: this.dataset.labelSelect,
      date: this.dataset.labelDate,
      past: this.dataset.labelPast,
      add: this.dataset.labelAdd,
      adding: this.dataset.labelAdding,
      added: this.dataset.labelAdded,
    };
    this.multiMin = Number(this.dataset.multiMin) || 2;
    this.multiPct = Number(this.dataset.multiPct) || 0;
    this.cartCount = Number(this.dataset.cartCount) || 0;
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: this.dataset.currency || 'GBP',
    });
    this.state = { line: '', lineName: '', ship: '', date: '', qty: 1, noDate: false, variant: this.refs.variantId.value };

    // Keep every ship option so the select can be rebuilt per line.
    this.shipOptions = Array.from(this.refs.ship.querySelectorAll('option[data-line]')).map((o) => ({
      line: o.dataset.line,
      name: o.value,
      label: o.textContent.trim(),
      comingSoon: o.dataset.comingSoon === 'true',
    }));
    // Ships always list A to Z, so Jane can add a ship anywhere in a cruise line's list.
    this.shipOptions.sort((x, y) => x.name.localeCompare(y.name, 'en-GB', { sensitivity: 'base', numeric: true }));
    this.refs.ship.querySelectorAll('option[data-line]').forEach((o) => o.remove());
    this.lockedPlaceholder = this.refs.ship.options[0]?.textContent || '';
    this.shipPlaceholder = this.refs.ship.dataset.placeholder || 'Select your ship';

    this.pastForm = false;
    if (this.refs.stPrompt && 'IntersectionObserver' in window) {
      // Once the form has scrolled up out of view and nothing is chosen yet, the bar invites
      // the visitor to start an order. Horizon scrolls the page inside .page-wrapper on desktop.
      const wrapper = document.querySelector('.page-wrapper');
      const root = window.matchMedia('(min-width: 990px)').matches ? wrapper : null;
      this.promptObserver = new IntersectionObserver(([entry]) => {
        this.pastForm = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.syncSticky();
      }, { root, threshold: 0 });
      this.promptObserver.observe(this.form);
      this.refs.stPromptBtn?.addEventListener('click', () => {
        this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.refs.lines.querySelector('.cc-swatch')?.focus({ preventScroll: true });
      });
    }
    if (this.refs.sticky && 'ResizeObserver' in window) {
      this.stickyObserver = new ResizeObserver(() => this.syncStickyHeight());
      this.stickyObserver.observe(this.refs.sticky);
    }

    this.setMinDate();
    this.bind();
    this.restore();
    this.syncQty();
    this.updateVariant();
    this.checkReady();
  }

  bind() {
    this.refs.lines.addEventListener('click', (e) => {
      const btn = e.target.closest('.cc-swatch');
      if (btn) this.selectLine(btn.dataset.line, btn.dataset.name);
    });
    document.addEventListener('cc:select-line', (e) => {
      const { line, name } = e.detail || {};
      if (line) {
        this.selectLine(line, name);
        this.closest('section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    this.refs.ship.addEventListener('change', () => {
      this.state.ship = this.refs.ship.value;
      this.refs.propShip.value = this.state.ship;
      this.state.date = '';
      this.refs.date.value = '';
      this.refs.propDate.value = '';
      this.refs.date.disabled = !this.state.ship;
      if (this.state.ship) {
        void this.refs.date.offsetWidth;
        this.refs.date.focus({ preventScroll: true });
      }
      this.setNoDateActive(false);
      this.checkReady();
    });
    this.refs.date.addEventListener('change', () => {
      this.state.date = this.refs.date.value;
      this.refs.propDate.value = orderDate(this.state.date);
      this.setNoDateActive(!!this.state.date);
      this.checkReady();
    });
    this.refs.qtyDec.addEventListener('click', () => { if (this.state.qty > 1) { this.state.qty--; this.syncQty(); } });
    this.refs.qtyInc.addEventListener('click', () => { if (this.state.qty < 20) { this.state.qty++; this.syncQty(); } });
    this.querySelectorAll('input[name="cc-variant"]').forEach((r) =>
      r.addEventListener('change', () => { this.state.variant = r.value; this.updateVariant(); }),
    );
    this.refs.noDate.addEventListener('change', () => {
      this.state.noDate = this.refs.noDate.checked;
      this.refs.propPrint.value = this.state.noDate ? 'No' : 'Yes';
      this.updateSticky();
      this.save();
    });
    // Enter on a tick box or a variant would submit the form (the browser's implicit
    // submission). Keyboard users expect it to tick the box instead.
    this.form.addEventListener('keydown', (e) => {
      const t = e.target;
      if (e.key !== 'Enter' || !(t instanceof HTMLInputElement)) return;
      if (t.type === 'checkbox') { e.preventDefault(); t.click(); }
      else if (t.type === 'radio') { e.preventDefault(); if (!t.checked) t.click(); }
    });
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  setMinDate() {
    const d = new Date();
    this.today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    this.refs.date.min = this.today;
  }

  selectLine(line, name) {
    const btn = this.refs.lines.querySelector(`.cc-swatch[data-line="${CSS.escape(line)}"]`);
    if (!btn) return;
    this.state.line = line;
    this.state.lineName = name || btn.dataset.name;
    this.refs.propLine.value = this.state.lineName;
    this.refs.lines.querySelectorAll('.cc-swatch').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    // Scroll the strip itself; scrollIntoView would also scroll the page to the form on load.
    const strip = this.refs.lines;
    strip.scrollTo({ left: Math.max(0, btn.offsetLeft - (strip.clientWidth - btn.offsetWidth) / 2), behavior: 'smooth' });
    this.populateShips(line);
    this.state.ship = '';
    this.refs.propShip.value = '';
    this.state.date = '';
    this.refs.date.value = '';
    this.refs.propDate.value = '';
    this.refs.date.disabled = true;
    this.setNoDateActive(false);
    this.checkReady();
  }

  populateShips(line) {
    const sel = this.refs.ship;
    sel.innerHTML = '';
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = this.shipPlaceholder;
    sel.appendChild(ph);
    for (const s of this.shipOptions.filter((o) => o.line === line)) {
      const o = document.createElement('option');
      o.value = s.name;
      o.textContent = s.label;
      if (s.comingSoon) o.disabled = true;
      sel.appendChild(o);
    }
    sel.disabled = false;
  }

  setNoDateActive(on) {
    this.refs.noDateWrap.classList.toggle('cc-date-opt--inactive', !on);
    this.refs.noDate.disabled = !on;
    if (!on && this.refs.noDate.checked) {
      this.refs.noDate.checked = false;
      this.state.noDate = false;
      this.refs.propPrint.value = 'Yes';
    }
  }

  syncQty() {
    this.refs.qtyVal.textContent = String(this.state.qty);
    this.refs.quantity.value = String(this.state.qty);
    this.refs.qtyDec.disabled = this.state.qty <= 1;
    this.updateSticky();
    this.checkReady();
  }

  updateVariant() {
    this.refs.variantId.value = this.state.variant;
    this.querySelectorAll('.cc-tier').forEach((t) => t.classList.toggle('is-checked', t.querySelector('input').value === this.state.variant));
    this.updateSticky();
    this.save();
  }

  unitPrice() {
    const r = this.querySelector(`input[name="cc-variant"][value="${this.state.variant}"]`);
    return Number(r?.dataset.price) || 0;
  }

  dateIsPast() {
    if (!this.state.date) return false;
    return this.state.date < this.today;
  }

  fmtDate(iso) {
    const d = new Date(`${iso}T12:00:00`);
    return isNaN(d) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  checkReady() {
    const { submit, dateError } = this.refs;
    let ready = false;
    let label = this.labels.select;
    if (this.state.ship && !this.state.date) label = this.labels.date;
    if (this.state.ship && this.state.date && this.dateIsPast()) label = this.labels.past;
    if (this.state.ship && this.state.date && !this.dateIsPast()) {
      ready = true;
      label = (this.labels.add || 'Add to basket')
        .replace('{ship}', this.state.qty >= 2 ? `${this.state.qty} × ${this.state.ship}` : this.state.ship)
        .replace('{qty}', String(this.state.qty));
    }
    dateError.hidden = !(this.state.date && this.dateIsPast());
    submit.disabled = !ready;
    this.refs.submitText.textContent = label;
    this.ready = ready;
    this.syncSticky();
    this.updateSticky();
    this.save();
  }

  updateSticky() {
    const r = this.refs;
    if (!r.stPrice) return;
    const unit = this.unitPrice();
    const qty = this.state.qty;
    let total = unit * qty;
    const qualifies = this.multiPct > 0 && qty + this.cartCount >= this.multiMin;
    if (qualifies) {
      r.stOrig.textContent = this.money.format(total / 100);
      r.stOrig.hidden = false;
      r.stBadge.hidden = false;
      total = Math.round(total * (1 - this.multiPct / 100));
    } else {
      r.stOrig.hidden = true;
      r.stBadge.hidden = true;
    }
    r.stPrice.textContent = this.money.format(total / 100);
    r.stLine.textContent = this.state.lineName;
    r.stShip.textContent = this.state.ship;
    r.stDate.textContent = this.state.date ? this.fmtDate(this.state.date) : '';
    r.stNoDate.hidden = !this.state.noDate;
  }

  save() {
    try { localStorage.setItem(STATE_KEY, JSON.stringify(this.state)); } catch (e) { /* private mode */ }
  }

  restore() {
    let s;
    try { s = JSON.parse(localStorage.getItem(STATE_KEY) || 'null'); } catch (e) { return; }
    if (!s || !s.line) return;
    const btn = this.refs.lines.querySelector(`.cc-swatch[data-line="${CSS.escape(s.line)}"]`);
    if (!btn) return;
    this.selectLine(s.line, s.lineName);
    if (s.ship && this.shipOptions.some((o) => o.line === s.line && o.name === s.ship && !o.comingSoon)) {
      this.refs.ship.value = s.ship;
      this.state.ship = s.ship;
      this.refs.propShip.value = s.ship;
      this.refs.date.disabled = false;
    }
    if (s.date && s.date >= this.today && this.state.ship) {
      this.refs.date.value = s.date;
      this.state.date = s.date;
      this.refs.propDate.value = orderDate(s.date);
      this.setNoDateActive(true);
      if (s.noDate) { this.refs.noDate.checked = true; this.state.noDate = true; this.refs.propPrint.value = 'No'; }
    }
    if (s.qty >= 1 && s.qty <= 20) this.state.qty = s.qty;
    const radio = this.querySelector(`input[name="cc-variant"][value="${s.variant}"]`);
    if (radio && !radio.disabled) { radio.checked = true; this.state.variant = s.variant; }
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.refs.submit.disabled) return;
    const { submit, submitText, formError, stBtnText } = this.refs;
    const formData = new FormData(this.form);
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    submit.classList.add('is-busy');
    const readyLabel = submitText.textContent;
    submitText.textContent = this.labels.adding || readyLabel;
    if (stBtnText) stBtnText.textContent = this.labels.adding || stBtnText.textContent;
    formError.hidden = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: this.state.qty }],
        promise: deferred.promise,
      }),
    );

    try {
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const json = await res.json();
      if (json.status) throw new Error(json.description || json.message || 'Could not add to basket');

      const cart = await fetch(window.Theme?.routes?.cart_url ? `${window.Theme.routes.cart_url}.js` : '/cart.js').then((r) => r.json());
      this.cartCount = cart.item_count;
      this.dataset.cartCount = String(cart.item_count);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cc-buy-form',
          sourceId: this.id,
          itemCount: this.state.qty,
          productId: this.dataset.productId,
          sections: json.sections,
          didError: false,
        },
      });

      submitText.textContent = this.labels.added || readyLabel;
      if (window.Shopify?.actions?.openCart) {
        window.Shopify.actions.openCart();
      } else {
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        if (drawer?.open) drawer.open();
      }
      setTimeout(() => { submit.classList.remove('is-busy'); this.checkReady(); if (stBtnText) stBtnText.textContent = this.refs.stBtn.dataset.label || 'Add to basket'; }, 1200);
    } catch (err) {
      deferred.reject(err);
      this.dispatchEvent(new CartErrorEvent({ error: err.message, code: 'INVALID' }));
      formError.textContent = err.message;
      formError.hidden = false;
      submit.classList.remove('is-busy');
      submitText.textContent = readyLabel;
      if (stBtnText) stBtnText.textContent = this.refs.stBtn.dataset.label || 'Add to basket';
    }
  }

  /**
   * Publishes the sticky bar's height as --cc-sticky-height on <html> so the page can
   * reserve that much room at the bottom (see snippets/cc-head.liquid). Re-measured
   * whenever the bar resizes, e.g. on rotation or when the text wraps.
   */
  /** Shows the bar in one of two states: the order summary when the form is complete, or the
   *  start-your-order prompt when nothing is chosen and the form has scrolled out of view. */
  syncSticky() {
    const bar = this.refs.sticky;
    if (!bar) return;
    const on = !!this.ready;
    const prompt = !on && !!this.refs.stPrompt && this.pastForm;
    bar.classList.toggle('is-on', on);
    bar.classList.toggle('is-prompt', prompt);
    bar.setAttribute('aria-hidden', String(!(on || prompt)));
    bar.inert = !(on || prompt);
    this.syncStickyHeight();
  }

  syncStickyHeight() {
    const bar = this.refs.sticky;
    if (!bar) return;
    const on = bar.classList.contains('is-on') || bar.classList.contains('is-prompt');
    document.documentElement.style.setProperty('--cc-sticky-height', on ? `${bar.offsetHeight}px` : '0px');
  }

  disconnectedCallback() {
    this.stickyObserver?.disconnect();
    this.promptObserver?.disconnect();
    document.documentElement.style.removeProperty('--cc-sticky-height');
  }
}

if (!customElements.get('cc-buy-form')) customElements.define('cc-buy-form', CCBuyForm);
